import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";

import { ReasonKinds } from "@featbit/js-client-sdk";

import { FeatureFlagContext, getActiveToken, isFeatureFlagsEnabled, useFeatureFlag, useUserContext } from "utils";

import { FeatureFlagKey } from "consts";

// a FeatBit outage must not hide the gated part forever, so after this the flag is used as it is
export const DEFAULT_FALLBACK_AFTER_MS = 3000;

type FeatureFlagGateProps = {
  flag: FeatureFlagKey;
  /** 0 for a gate inside a gate that has already waited for FeatBit: it answers at once, as its parent did */
  fallbackAfterMs?: number;
  children: (enabled: boolean) => ReactNode;
};

type SettledAnswer = { userId: string; enabled: boolean };

/**
 * Renders once FeatBit answers for the signed-in user, so a layout that depends on the flag never paints one way and flips.
 * The answer sticks for that user: after a farm switch FeatBit identifies the user again and answers from an empty store
 * until their flags arrive, which must not blink the gated part out and in.
 */
export const FeatureFlagGate = ({ flag, fallbackAfterMs = DEFAULT_FALLBACK_AFTER_MS, children }: FeatureFlagGateProps) => {
  const { isEnabled, kind, isForced } = useFeatureFlag(flag);
  const { identifiedKeyId, identifying } = useContext(FeatureFlagContext);
  const { user } = useUserContext();
  const [waitedLongEnough, setWaitedLongEnough] = useState(false);
  const settled = useRef<SettledAnswer | null>(null);

  // right after sign-in the flags still answer for the anonymous visitor, so we wait until FeatBit knows this user
  const token = getActiveToken();
  const awaitingIdentity = !!token && token !== "null" && (!user?.id || identifiedKeyId !== user.id);
  // a flag forced by the E2E override never comes from FeatBit, so it renders at once
  const resolving = !isForced && !!user && isFeatureFlagsEnabled() && (kind === ReasonKinds.ClientNotReady || awaitingIdentity || identifying);

  useEffect(() => {
    if (!resolving || fallbackAfterMs === 0) return undefined;

    setWaitedLongEnough(false);
    const timer = window.setTimeout(() => setWaitedLongEnough(true), fallbackAfterMs);
    return () => window.clearTimeout(timer);
  }, [resolving, fallbackAfterMs]);

  if (!resolving && user?.id) settled.current = { userId: user.id, enabled: isEnabled };
  const settledForUser = user?.id && settled.current?.userId === user.id ? settled.current : null;

  if (resolving && settledForUser) return <>{children(settledForUser.enabled)}</>;
  if (resolving && fallbackAfterMs > 0 && !waitedLongEnough) return null;

  return <>{children(isEnabled)}</>;
};
